import template from './documentTemplate.json';
import {
  ONGEVALLEN_DEFAULTS,
  ONGEVALLEN_VW_CODE,
  sumsForCombination,
  combinationForSums,
  combinationForInvalid,
  kidsSumsForCombination,
  kidsCombinationForInvalid,
  GSS_WITH_CHILDREN,
  GSS_WITH_PARTNER,
} from './fields';
import { getProductEntry } from '../../../products/registry';
import { computeDoorlopendeReisDisplayPremium } from '../../../utils/doorlopendeReisPricing';
import { sanitizeDocument } from '../doorlopendereis/calc';

export { extractEngineMessages } from '../doorlopendereis/calc';

// Product wiring (carrier, product name, calc endpoint) comes from the central registry.
const ONGEVALLEN = getProductEntry('ongevallen')!;

export const ONGEVALLEN_CALC_ENDPOINT = ONGEVALLEN.calcEndpoint;

export interface VpAddress {
  VP_PCODE?: string;
  VP_HUISNR?: string | number;
  VP_TOEVOEG?: string;
  VP_STRAAT?: string;
  VP_PLAATS?: string;
}

export interface OngevallenPremiums {
  display: number | null;
  OD_NTP: number | null;
  OD_OVERL: number | null;
  OD_INVALID: number | null;
  PP_NTP: number | null;
  PP_TTOT: number | null;
}

const clone = <T>(o: T): T => JSON.parse(JSON.stringify(o));

/**
 * Recursively sort object keys alphabetically (AFD convention). Array order is kept;
 * each element's keys are sorted. Applied to every outgoing ongevallen document:
 * calculation, offer chain (via enrich) and the final request.
 */
export const sortAfdDocument = (value: any): any => {
  if (Array.isArray(value)) return value.map(sortAfdDocument);
  if (value && typeof value === 'object') {
    const sorted: any = {};
    for (const k of Object.keys(value).sort()) sorted[k] = sortAfdDocument(value[k]);
    return sorted;
  }
  return value;
};

/**
 * Sort the ongevallen contract(s) inside a Relatiedocument. The offer chain replaces
 * its working document with each BFF response (unsorted), so this runs right before
 * every outgoing call. Other products' contracts are left untouched.
 */
export const sortOngevallenContracts = (relatiedocument: any): any => {
  if (Array.isArray(relatiedocument?.Contract)) {
    relatiedocument.Contract = relatiedocument.Contract.map((c: any) =>
      c?.PP?.OD && !c.PP.DR ? sortAfdDocument(c) : c,
    );
  }
  return relatiedocument;
};

const pad = (n: number) => String(n).padStart(2, '0');
const n8 = (d: Date) => Number(`${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`);

/** clientInfo dateOfBirth (DD-MM-JJJJ) → AFD N8 (JJJJMMDD); null when absent/invalid. */
export const gebdatN8 = (dateOfBirth?: string | null): number | null => {
  if (!dateOfBirth) return null;
  let dateStr = dateOfBirth.replace(/-/g, '');
  if (dateStr.length !== 8) return null;
  if (dateOfBirth.indexOf('-') === 2) {
    dateStr = dateStr.substring(4, 8) + dateStr.substring(2, 4) + dateStr.substring(0, 2);
  }
  const dateNum = Number(dateStr);
  return isNaN(dateNum) ? null : dateNum;
};

const routeValue = (doc: any, key: string, value: string | number) => {
  const pp = doc.Contractdocument.PP;
  if (key.startsWith('OD_')) pp.OD[key] = value;
  else if (key.startsWith('VZ_')) pp.VZ[key] = value;
  else if (key.startsWith('VE_')) pp.VE[key] = value;
  else if (key.startsWith('VP_')) pp.VP[key] = value;
  else if (key.startsWith('PP_')) pp[key] = value;
};

// Schema whitelists (ongevallen schema, 2026-07-14). Contract VP carries only these four —
// the full address travels on the Relatiedocument VP, not the contract.
const VP_SCHEMA_KEYS = ['VP_ANAAM', 'VP_GEBDAT', 'VP_NRHNDRG', 'VP_PCODE'] as const;
const VZ_SCHEMA_KEYS = ['VZ_BERMIL', 'VZ_BERMILP', 'VZ_GEBDAT'] as const;

const pickKeys = (obj: any, keys: readonly string[]): any => {
  const out: any = {};
  for (const k of keys) if (obj?.[k] !== undefined) out[k] = obj[k];
  return out;
};

export interface OngevallenSums {
  OD_COMBINA: string;
  OD_OVERL: number;
  OD_INVALID: number;
  OD_COMBIKN: string;
  OD_VBBIKND?: number;
  OD_OVERLKND?: number;
}

/**
 * The combination code + insured sums for a set of screen values, normalized the way the
 * engine cross-validates them (fields.ts COMBINATIONS; the original product form shows
 * OD_COMBINA next to both sum fields and leaves that validation to the engine — see the
 * uiSchema). A pair from the pricing table is normalized onto its code; any other pair is
 * kept AS-IS (code best-effort from the invaliditeit sum) so the engine's own rejection
 * reaches the user. Records saved before the sums were separate fields only carry
 * OD_COMBINA — the sums derive from it. Kids sums only exist for a gezinssamenstelling with
 * children; OD_COMBIKN is schema-required regardless and the engine only allows 1-3 there
 * (adult codes 4-6 are rejected: "ongeldige waarde"), so a childless GSS mirrors the adult
 * code when it is a valid kids code, else clamps to 1.
 *
 * ONE source for the calculate payload AND for the later touchpoints that re-assert these
 * user inputs on a stored contract (offer chain, final request — restoreOngevallenInputs).
 */
export const resolveOngevallenSums = (
  values: Record<string, string | number | undefined>,
): OngevallenSums => {
  const hasChildren = (GSS_WITH_CHILDREN as readonly string[]).includes(String(values.OD_GSS ?? ''));
  const storedCode = values.OD_COMBINA ?? ONGEVALLEN_DEFAULTS.OD_COMBINA;

  let adult: { code: string; overl: number; invalid: number };
  if (values.OD_OVERL != null && values.OD_INVALID != null) {
    const match =
      combinationForSums(values.OD_OVERL, values.OD_INVALID) ??
      combinationForInvalid(values.OD_INVALID) ??
      sumsForCombination(storedCode);
    adult = { code: match.code, overl: Number(values.OD_OVERL), invalid: Number(values.OD_INVALID) };
  } else {
    adult = sumsForCombination(storedCode);
  }

  const sums: OngevallenSums = {
    OD_COMBINA: adult.code,
    OD_OVERL: adult.overl,
    OD_INVALID: adult.invalid,
    OD_COMBIKN: kidsSumsForCombination(adult.code).code,
  };
  if (hasChildren) {
    const kids =
      kidsCombinationForInvalid(values.OD_VBBIKND) ??
      kidsSumsForCombination(values.OD_COMBIKN ?? ONGEVALLEN_DEFAULTS.OD_COMBIKN);
    sums.OD_COMBIKN = kids.code;
    sums.OD_VBBIKND = kids.invalid;
    sums.OD_OVERLKND = kids.overl;
  }
  return sums;
};

const isBlank = (v: unknown): boolean => v == null || v === '';

/**
 * Re-assert the user's OD inputs on a stored contract's OD section. The clientservice
 * responses the offer chain and the final request are built from (relation calc → package
 * → quote) do not echo input-only fields, so OD_COMBINA (schema-required, codes '1'-'6')
 * and the insured sums can be missing there. Fills only what the stored copy lost —
 * engine-echoed values stay — and pins the combination codes to strings (their schema
 * type), so a numeric echo never reaches ANVA as 1 instead of '1'.
 */
export const restoreOngevallenInputs = (
  od: any,
  values?: Record<string, string | number> | null,
): any => {
  const out: any = { ...(od || {}) };
  if (values) {
    const sums = resolveOngevallenSums(values);
    for (const [key, value] of Object.entries(sums)) {
      if (isBlank(out[key])) out[key] = value;
    }
    if (isBlank(out.OD_GSS) && !isBlank(values.OD_GSS)) out.OD_GSS = values.OD_GSS;
  }
  for (const key of ['OD_COMBINA', 'OD_COMBIKN']) {
    if (!isBlank(out[key])) out[key] = String(out[key]);
  }
  return out;
};

export interface OngevallenInsuredPerson {
  lastName?: string | null;
  initials?: string | null;
  gebdat?: number | null;
}

/**
 * The verzekerde's identity on the VZ block: VZ_ANAAM / VZ_VOORL (and VZ_GEBDAT from the
 * policyholder when absent). For the offer-chain and final-request documents ONLY — the
 * K781 calculate schema allows nothing but VZ_BERMIL/VZ_BERMILP/VZ_GEBDAT on VZ
 * (additionalProperties: false), so buildOngevallenDocument must never pick this up.
 * Fills only what is absent.
 */
export const restoreOngevallenInsured = (vz: any, person: OngevallenInsuredPerson): any => {
  const out: any = { ...(vz || {}) };
  if (isBlank(out.VZ_ANAAM) && person.lastName) out.VZ_ANAAM = person.lastName;
  if (isBlank(out.VZ_VOORL) && person.initials) out.VZ_VOORL = person.initials;
  if (isBlank(out.VZ_GEBDAT) && person.gebdat != null) out.VZ_GEBDAT = person.gebdat;
  return out;
};

/** Build the Contractdocument the BFF expects from the flat screen values + address. */
export const buildOngevallenDocument = (
  values: Record<string, string | number>,
  vp?: VpAddress | null,
): any => {
  const doc: any = clone(template);

  // Defaults merged in so the request is self-sufficient regardless of whether the
  // screen has persisted them into step data yet (BFF marks them verplicht).
  const vals: Record<string, string | number> = { ...ONGEVALLEN_DEFAULTS, ...values };

  vals.PP_BETTERM = ONGEVALLEN_DEFAULTS.PP_BETTERM;

  const gss = String(vals.OD_GSS ?? '');
  const hasPartner = (GSS_WITH_PARTNER as readonly string[]).includes(gss);

  // Combination code + sums, normalized the way the engine cross-validates them (see
  // resolveOngevallenSums). Kids sums only travel for a gezinssamenstelling with children.
  const sums = resolveOngevallenSums(values);
  Object.assign(vals, sums);
  if (sums.OD_VBBIKND === undefined) {
    delete vals.OD_VBBIKND;
    delete vals.OD_OVERLKND;
  }

  vals.VZ_BERMIL = String(vals.VZ_BERMIL ?? 'N');
  // Partner beroepsmilitair may only be filled when there IS a partner — the engine
  // rejects it otherwise ("VZ_BERMILP mag niet gevuld zijn").
  if (hasPartner) vals.VZ_BERMILP = String(vals.VZ_BERMILP ?? 'N');
  else delete vals.VZ_BERMILP;

  Object.entries(vals).forEach(([k, v]) => routeValue(doc, k, v));

  // Contract VP: the schema allows only VP_ANAAM/VP_GEBDAT/VP_NRHNDRG/VP_PCODE — from the
  // address block only the postcode qualifies. Street/city/number reach ANVA on the
  // Relatiedocument VP (relation flow), where the full address belongs.
  if (vp?.VP_PCODE) doc.Contractdocument.PP.VP.VP_PCODE = vp.VP_PCODE;
  doc.Contractdocument.PP.VP = pickKeys(doc.Contractdocument.PP.VP, VP_SCHEMA_KEYS);
  doc.Contractdocument.PP.VZ = pickKeys(doc.Contractdocument.PP.VZ, VZ_SCHEMA_KEYS);

  // Acceptance answers (RI) are schema-required at calculation time too. The real answers
  // only exist after the acceptance screen (which mirrors them onto RI_OGBOOIT/RI_POLJUS8/
  // RI_OVRMED); price-neutral 'N' placeholders keep the calculation document valid.
  doc.Contractdocument.PP.RI = { RI_OGBOOIT: 'N', RI_OVRMED: 'N', RI_POLJUS8: 'N' };

  // Voorwaarden: schema requires >= 1 VW row. Seed the ANVA-registered code; the engine
  // replaces it with its own row (unregistered spelling — normalizeOngevallenVwCodes).
  doc.Contractdocument.PP.VW = [{ VW_VRWNR: ONGEVALLEN_VW_CODE }];

  // Required dates (N8): ingang = today, hoofdvervaldatum = 1st of the same month next year.
  const now = new Date();
  const ingang = n8(now);
  doc.Contractdocument.PP.PP_INGDAT = ingang;
  doc.Contractdocument.PP.PP_INGDTW = ingang;
  doc.Contractdocument.PP.PP_HVVDAT = Number(`${now.getFullYear() + 1}${pad(now.getMonth() + 1)}01`);

  // PP_PRODNM only — PP_PRODUCT is not in the schema (the BFF re-adds it on responses).
  // AL app metadata (AL_APPLNM/AL_URLNAAM) likewise dropped: the AL section only allows
  // BY/AL_DATACAT/AL_FUNCTIE and the BFF resolves the brand from the tenant headers.
  doc.Contractdocument.PP.PP_PRODNM = ONGEVALLEN.productName;

  // XG is a BFF *response* container (status/messages) — never send it in.
  delete doc.Contractdocument.XG;

  const contract = sanitizeDocument(doc.Contractdocument);
  // Schema-required members whose empty values sanitizeDocument strips: an empty TP/VL
  // object and the not-yet-known IBAN must still be present for the document to validate.
  contract.PP.TP = contract.PP.TP ?? {};
  contract.PP.VL = contract.PP.VL ?? {};
  contract.PP.PP_IBAN = contract.PP.PP_IBAN ?? '';

  return { Contractdocument: sortAfdDocument(contract) };
};

export const buildOngevallenRequest = (
  values: Record<string, string | number>,
  vp?: VpAddress | null,
) => ({
  carrierCode: ONGEVALLEN.carrierCode,
  productName: ONGEVALLEN.productName,
  input: buildOngevallenDocument(values, vp),
});

// The K781 engine emits the voorwaarden code as 'SAA_DFDDON.2025.01'; the ANVA
// poliscodelabel 10698 registration is 'SAA.DFDON.2025.01' — dot separators AND one D
// (DFDON, not DFDDON; error 90356 proved both engine spellings unregistered).
// Normalize wherever the document flows onward.
// TODO(bff): correct the code in the K781 engine definition so this mapping can go.
export const normalizeOngevallenVwCodes = (contract: any): any => {
  const vw = contract?.PP?.VW;
  if (Array.isArray(vw)) {
    for (const row of vw) {
      if (typeof row?.VW_VRWNR !== 'string') continue;
      let code = row.VW_VRWNR;
      if (code.startsWith('SAA_')) code = 'SAA.' + code.slice(4);
      code = code.replace('SAA.DFDDON.', 'SAA.DFDON.');
      row.VW_VRWNR = code;
    }
  }
  return contract;
};

const num = (v: any): number | null => (typeof v === 'number' ? v : v != null && v !== '' ? Number(v) : null);

/** Pull the display premium and the priced insured sums out of a calc response. */
export const extractPremiums = (response: any): OngevallenPremiums => {
  const pp = response?.output?.Contractdocument?.PP;
  return {
    display: computeDoorlopendeReisDisplayPremium(pp),
    OD_NTP: num(pp?.OD?.OD_NTP),
    OD_OVERL: num(pp?.OD?.OD_OVERL),
    OD_INVALID: num(pp?.OD?.OD_INVALID),
    PP_NTP: num(pp?.PP_NTP),
    PP_TTOT: num(pp?.PP_TTOT),
  };
};
